import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import styles from "./CustomerReviews.module.css";
import WriteReviewModal from "./WriteReviewModal";
import { fetchReviewsList } from "@/services/api/common";
import { FaCalendarAlt, FaStar } from "react-icons/fa";
import { PiSealCheckFill } from "react-icons/pi";
import { BiSolidEditAlt } from "react-icons/bi";
import { withRegionPath } from "@/utils/session";
import ReviewAvatar from "./ReviewAvatar";
import { formatReviewDateRelative, formatSlugLabel } from "./reviewUtils";

function Stars({ count }) {
  const safeCount = Math.max(0, Math.min(5, Math.round(Number(count) || 0)));

  return (
    <div className={styles.stars} aria-label={`${safeCount} out of 5 stars`}>
      {Array.from({ length: safeCount }).map((_, i) => (
        <FaStar key={i} color="#FCAB45" />
      ))}
    </div>
  );
}

export function ReviewCard({
  review,
  slugLabel = "",
  className = "",
  textClassName = "",
  showQuotes = false,
}) {
  const badgeText =
    review.tripToCity ||
    review.packageName ||
    review.trip ||
    (slugLabel ? `Trip To ${slugLabel}` : "");

  const rawText = review.reviewText?.trim() || "";
  const displayText =
    showQuotes && rawText && !/^["“]/.test(rawText) ? `${rawText}` : rawText;

  return (
    <article className={`${styles.card} ${className}`.trim()}>
      <Stars count={review.rating} />
      <p className={`${styles.text} ${textClassName}`.trim()}>{displayText}</p>
      <div className={styles.divider} />
      <div className={styles.meta}>
        <ReviewAvatar
          imageUrl={review.userImageUrl}
          name={review.userName}
          size={40}
          className={styles.avatar}
        />
        <div className={styles.user}>
          <p className={styles.name}>{review.userName}</p>
          <div className={styles.userInfo}>
            {badgeText ? (
              <span className={styles.badge} title={badgeText}>
                <PiSealCheckFill />
                <span className={styles.badgeText}>{badgeText}</span>
              </span>
            ) : null}
            <span className={styles.time}>
              <FaCalendarAlt />
              {formatReviewDateRelative(review.reviewedOn)}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

export function ReviewsMarquee({
  reviews = [],
  slugLabel = "",
  showQuotes = false,
  getSlugLabel,
}) {
  const marqueeRef = useRef(null);
  const setRef = useRef(null);
  const [copies, setCopies] = useState(2);
  const [travel, setTravel] = useState(0);
  const marqueeDuration = Math.max(40, Math.round((travel || 1200) / 40));

  useEffect(() => {
    if (!reviews.length) return undefined;

    const measure = () => {
      const setWidth = setRef.current?.offsetWidth || 0;
      const viewWidth = marqueeRef.current?.offsetWidth || 0;
      if (!setWidth) return;

      const nextTravel = setWidth;
      const nextCopies = Math.max(2, Math.ceil(viewWidth / setWidth) + 1);
      setTravel((prev) => (prev === nextTravel ? prev : nextTravel));
      setCopies((prev) => (prev === nextCopies ? prev : nextCopies));
    };

    measure();
    const frame = window.requestAnimationFrame(measure);
    window.addEventListener("resize", measure);

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      if (marqueeRef.current) observer.observe(marqueeRef.current);
      if (setRef.current) observer.observe(setRef.current);
    }

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [reviews.length]);

  if (!reviews.length) return null;

  return (
    <div
      ref={marqueeRef}
      className={styles.marquee}
      aria-label="Customer reviews carousel"
    >
      <div
        className={styles.track}
        style={{
          "--marquee-duration": `${marqueeDuration}s`,
          "--marquee-travel": travel ? `${travel}px` : "50%",
        }}
      >
        {Array.from({ length: copies }, (_, copyIndex) => (
          <div
            key={`set-${copyIndex}`}
            ref={copyIndex === 0 ? setRef : null}
            className={styles.set}
          >
            {reviews.map((review, reviewIndex) => (
              <ReviewCard
                key={`${review.reviewId || reviewIndex}-${copyIndex}`}
                review={review}
                slugLabel={getSlugLabel ? getSlugLabel(review) : slugLabel}
                showQuotes={showQuotes}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CustomerReviews({
  moduleType,
  slug: slugProp = "",
  initialReviewsList = null,
  title = "Customer Reviews",
  subtitle = "Travelers sharing unforgettable holiday moments!",
  viewAllHref = "",
}) {
  const router = useRouter();
  const routerSlug = router.query.slug;
  const slug =
    slugProp ||
    (Array.isArray(routerSlug) ? routerSlug[0] : routerSlug) ||
    "";
  const [reviewsList, setReviewsList] = useState(initialReviewsList);
  const [isLoaded, setIsLoaded] = useState(initialReviewsList != null);
  const [showWriteModal, setShowWriteModal] = useState(false);

  const loadReviews = useCallback(() => {
    return fetchReviewsList({ moduleType, slug })
      .then((response) => {
        setReviewsList(response?.data || null);
      })
      .catch((error) => {
        console.error("Failed to load reviews list:", error);
        setReviewsList(null);
      })
      .finally(() => {
        setIsLoaded(true);
      });
  }, [moduleType, slug]);

  useEffect(() => {
    if (initialReviewsList != null) return;

    loadReviews();
  }, [initialReviewsList, loadReviews]);

  const reviews = reviewsList?.reviews || [];
  const slugLabel = formatSlugLabel(slug);
  const hasItems = reviews.length > 0;
  const showEmpty = isLoaded && !hasItems;

  if (!moduleType) {
    return null;
  }

  return (
    <div className="standard-padding">
      <section className={styles.section} aria-labelledby="customer-reviews-title">
        <div className={styles.header}>
          <div className={styles.headingBlock}>
            <h2 id="customer-reviews-title" className={"standard-heading"}>
              {title}
            </h2>
            {subtitle ? (
              <p className="standard-subheading">{subtitle}</p>
            ) : null}
          </div>
          {viewAllHref ? (
            <Link href={withRegionPath(viewAllHref)} className={styles.viewAllBtn}>
              View All
            </Link>
          ) : (
            <>
              <button
                type="button"
                className={styles.writeBtn}
                onClick={() => setShowWriteModal(true)}
              >
                Write a Review
              </button>
              <button
                type="button"
                className={styles.writeBtnMobile}
                onClick={() => setShowWriteModal(true)}
              >
                <BiSolidEditAlt />
                Write a Review
              </button>
            </>
          )}
        </div>

        {showEmpty ? (
          <p className="nodata">No data found</p>
        ) : hasItems ? (
          <ReviewsMarquee reviews={reviews} slugLabel={slugLabel} />
        ) : null}

        <WriteReviewModal
          open={showWriteModal}
          onClose={() => setShowWriteModal(false)}
          moduleType={moduleType}
          slug={slug}
          onSuccess={loadReviews}
          allowModuleSelect
        />
      </section>
    </div>
  );
}
